import { useEffect, useState } from "react";
import type { JSONSchema7 } from "json-schema";
import {
  type ERPProviderKey,
  type ConnectionCapabilities,
  ResolvedSyncConfigSchemaResponseSchema,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";

interface FetchResult {
  schema: JSONSchema7 | null;
  canScan: boolean;
  capabilities: ConnectionCapabilities | null;
}

/**
 * Fetches the resolved JSON Schema for an ERP provider's sync config, with
 * enum values populated from the connected account (e.g. HubSpot pipelines
 * and stages). For providers that don't customize the schema per-connection,
 * the same static schema as `selectedProvider.syncConfigSchema` is returned.
 * Also returns the selected connection's provider capabilities.
 * @param providerId - The ERP provider id (e.g. "hubspot")
 * @param connectionId - The connection id for the connected account
 * @returns The resolved JSON Schema and provider capabilities
 */
const fetchResolvedSyncConfigSchema = async (
  providerId: ERPProviderKey,
  connectionId: string,
): Promise<FetchResult> => {
  const response = await fetchApi(
    `integrations/erp/providers/${providerId}/sync-config-schema`,
    "GET",
    { params: { connectionId } },
  );

  if (!response.ok) {
    throw new Error(response.error ?? "Failed to load sync config schema");
  }

  const body = ResolvedSyncConfigSchemaResponseSchema.parse(response.data);
  return {
    schema: (body.schema as JSONSchema7 | null) ?? null,
    canScan: body.canScan ?? true,
    capabilities: body.capabilities ?? null,
  };
};

/**
 * Result of calling {@link useResolvedSyncConfigSchema}. `schema` is null when
 * the provider does not need per-connection schema resolution (callers should
 * fall back to the static `provider.syncConfigSchema` in that case).
 * `capabilities` is null when the provider does not return capability metadata.
 */
export interface UseResolvedSyncConfigSchemaResult {
  schema: JSONSchema7 | null;
  canScan: boolean;
  capabilities: ConnectionCapabilities | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the resolved sync-config JSON Schema for an ERP provider when the
 * provider customises the schema per-connection (currently HubSpot only).
 * Returns `null` schema for providers that do not need resolution — callers
 * should fall back to the provider's static `syncConfigSchema`. Also returns
 * provider capability metadata when available.
 * @param providerId - ERP provider id; pass null/undefined to skip
 * @param connectionId - Connection id for the connected account; pass null to skip
 * @returns The resolved schema, capabilities, loading flag, error message, and a refetch function
 */
export const useResolvedSyncConfigSchema = (
  providerId: ERPProviderKey | null | undefined,
  connectionId: string | null | undefined,
): UseResolvedSyncConfigSchemaResult => {
  const [schema, setSchema] = useState<JSONSchema7 | null>(null);
  const [canScan, setCanScan] = useState(true);
  const [capabilities, setCapabilities] =
    useState<ConnectionCapabilities | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!providerId || !connectionId) {
      setSchema(null);
      setCanScan(true);
      setCapabilities(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setSchema(null);
    setCanScan(true);
    setCapabilities(null);

    fetchResolvedSyncConfigSchema(providerId, connectionId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setSchema(result.schema);
        setCanScan(result.canScan);
        setCapabilities(result.capabilities);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setError(
          "Couldn't load sync configuration options. Check that the connection is still active.",
        );
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [providerId, connectionId, reloadKey]);

  return {
    schema,
    canScan,
    capabilities,
    isLoading,
    error,
    refetch: () => setReloadKey((key) => key + 1),
  };
};
